// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody
    ? { body: serialize(body) }
    : body !== undefined
    ? { body }
    : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        const msg = data?.message || data?.error || `Request failed with status ${res.status}`;
        const e = new Error(msg);
        Object.assign(e, data, { status: res.status });
        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Partial-refund cancellation for chat-offer-buy transactions in
// :state/captured-after-inspection-expiry. The BFF endpoint issues a Stripe partial refund
// (payinTotal minus Stripe fee) AND fires the corresponding Sharetribe
// transition for state tracking. The generic transition-privileged endpoint
// rejects these transitions, so this is the only legitimate path.
// Body: { transactionId, transitionName }.
export const cancelFromCaptured = body => {
  return post('/api/transactions/cancel-from-captured', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Server-orchestrated auth bridge. The server signs the user in to
// Supabase and Sharetribe in a single request: both `sb-*` and `st-*`
// cookies land on the same response.
export const loginToServer = body => post('/api/auth/login', body);
export const signupToServer = body => post('/api/auth/signup', body);
export const logoutFromServer = () => post('/api/auth/logout', {});

// Password recovery / reset (Supabase-backed)
export const recoverPasswordOnServer = body => post('/api/auth/recover-password', body);
export const verifyRecoveryOnServer = body => post('/api/auth/verify-recovery', body);
export const updatePasswordOnServer = body => post('/api/auth/update-password', body);

// Email verification (signup confirmation, email change)
export const verifySignupOnServer = tokenHash =>
  post('/api/auth/verify-signup', { token_hash: tokenHash });
export const verifyEmailChangeOnServer = tokenHash =>
  post('/api/auth/verify-email-change', { token_hash: tokenHash });
export const acceptInviteOnServer = body => post('/api/auth/accept-invite', body);
export const resendSignupConfirmation = email =>
  post('/api/auth/resend-signup-confirmation', { email });
export const changePassword = body => post('/api/auth/change-password', body);
export const changeEmail = body => post('/api/auth/change-email', body);
export const resendEmailChange = () => post('/api/auth/resend-email-change', {});
export const changePhone = body => post('/api/auth/change-phone', body);
export const verifyPhoneChange = body => post('/api/auth/verify-phone', body);
export const resendPhoneOtp = () => post('/api/auth/resend-phone-otp', {});
export const removePhone = body => post('/api/auth/remove-phone', body);

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

export const dashboardListingStats = body => {
  return post('/api/listings/stats', body);
};

/**
 * Preview the cancellation fee + net refund for a post-capture transaction.
 * Read-only; the BFF computes the figures. Body: { transactionId }.
 */
export const cancelFromCapturedPreview = body => {
  return post('/api/transactions/cancel-from-captured/preview', body);
};

export const fetchNotifications = () => request('/api/notifications', { method: 'GET' });

export const fetchCourierInboxToken = () => post('/api/notifications/courier-token', {});

export const setNotificationPreferenceToggle = body =>
  post('/api/notifications/preferences/toggle', body);

export const fetchNotificationPreferences = () =>
  request('/api/notifications/preferences', { method: 'GET' });

export const fetchOneSignalIdentity = () => post('/api/onesignal/identity', {});

export const checkSession = () => request('/api/auth/session-status', { method: 'GET' });

export const markTransactionRead = body => post('/api/transaction/mark-as-read', body);

export const mailchimpCreateContact = body => post('/api/mailchimp/create-contact', body);

export const updateListingSearchKeywords = listingId =>
  post('/api/listings/update-search-keywords', { listingId });

export const inboxSearch = (query, page = 1) => post('/api/inbox-search', { query, page });

/**
 * Fetch the signed-in user's referral program state from the BFF.
 *
 * @returns {Promise<{
 *   code: string|null,
 *   referralLink: string,
 *   credited: number,
 *   threshold: number,
 * }>}
 */
export const fetchReferralProgram = () =>
  request('/api/referrals/me', { method: 'GET', headers: { 'Content-Type': 'application/json' } });

/** JSON-body wrapper around `request`. Skips transit serialization. */
const jsonRequest = (path, { method = 'GET', body, headers = {} } = {}) =>
  request(path, {
    method,
    headers: { 'Content-Type': 'application/json', ...headers },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

/** GET /api/consent/policy/active */
export const fetchActiveConsentPolicy = () => jsonRequest('/api/consent/policy/active');

/** GET /api/auth/provisioning-status */
export const fetchProvisioningStatus = () => jsonRequest('/api/auth/provisioning-status');
/** POST /api/consent/record */
export const recordConsent = body => jsonRequest('/api/consent/record', { method: 'POST', body });
/** GET /api/consent/current */
export const fetchCurrentConsent = () => jsonRequest('/api/consent/current');
/** GET /api/consent/history */
export const fetchConsentHistory = () => jsonRequest('/api/consent/history');
/** POST /api/consent/withdraw */
export const withdrawConsent = body =>
  jsonRequest('/api/consent/withdraw', { method: 'POST', body: body || {} });
/** DELETE /api/consent/me */
export const deleteConsentSubject = () => jsonRequest('/api/consent/me', { method: 'DELETE' });

/** POST /api/transactions/pickup/verify */
export const verifyPickupCode = body =>
  jsonRequest('/api/transactions/pickup/verify', { method: 'POST', body });
/** GET /api/transactions/pickup/code?transactionId=... */
export const fetchPickupCode = transactionId =>
  jsonRequest(`/api/transactions/pickup/code?transactionId=${encodeURIComponent(transactionId)}`);
/** POST /api/transactions/pickup/reissue */
export const reissuePickupCode = body =>
  jsonRequest('/api/transactions/pickup/reissue', { method: 'POST', body });
/** POST /api/transactions/pickup/activate: buyer fires the privileged activate transition + mints OTP atomically. */
export const activatePickupCode = body =>
  jsonRequest('/api/transactions/pickup/activate', { method: 'POST', body });

export const startIdentityVerification = (body = {}) =>
  jsonRequest('/api/identity/start', { method: 'POST', body });

export const fetchIdentityStatus = sessionId =>
  request(
    sessionId
      ? `/api/identity/status?sessionId=${encodeURIComponent(sessionId)}`
      : '/api/identity/status',
    { method: 'GET' }
  );

/** POST /api/identity/checkout: one-time Stripe Checkout for the paid-identity fee (Basic users). */
export const startIdentityCheckout = () =>
  jsonRequest('/api/identity/checkout', { method: 'POST' });

/** GET /api/membership: sibling fetch dispatched from `fetchCurrentUser`. */
export const fetchMembership = () => jsonRequest('/api/membership');

/** POST /api/membership/basic: create the user's $0 Basic sub. */
export const startBasicMembership = () => jsonRequest('/api/membership/basic', { method: 'POST' });

/** POST /api/membership/checkout-session: Stripe Checkout URL for Pro/Business. */
export const startMembershipCheckout = body =>
  jsonRequest('/api/membership/checkout-session', { method: 'POST', body });

/** POST /api/membership/billing-portal-session: Stripe Billing Portal URL. */
export const openMembershipBillingPortal = body =>
  jsonRequest('/api/membership/billing-portal-session', { method: 'POST', body: body || {} });

/** POST /api/membership/sync: perm refresh + held-grant bind after a Stripe return. */
export const syncMembership = () => jsonRequest('/api/membership/sync', { method: 'POST' });

/** POST /api/membership/schedule-downgrade: schedule lower-tier switch for current_period_end. */
export const scheduleMembershipDowngrade = body =>
  jsonRequest('/api/membership/schedule-downgrade', { method: 'POST', body });

export const fetchSavedSearches = (page = 1, limit = 50) =>
  request(`/api/search-alerts?page=${page}&limit=${limit}`, { method: 'GET' });
export const createSavedSearch = body => post('/api/search-alerts', body);
export const updateSavedSearch = (id, body) =>
  request(`/api/search-alerts/${encodeURIComponent(id)}`, { method: 'PATCH', body });
export const deleteSavedSearch = id =>
  request(`/api/search-alerts/${encodeURIComponent(id)}`, { method: 'DELETE' });
